import React, { useEffect, useMemo } from 'react';
import { KIT_SLOT_GROUPS } from 'fym-shared/src/music24/assetKinds';
import { TypedMemo } from '../../../../common/types';
import { useGetAssetPublishesQuery } from '../../../../../redux/api/fymAPI';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { useFailuresSeen } from '../../work/assetsFailuresSeen';
import { AssetViewer } from '../../viewer/AssetViewer';
import { ReleaseTabProps } from '../releaseTabProps';
import { useKitPoll } from '../assets/useKitPoll';
import { useReleaseViewMark } from '../assets/useReleaseViewMark';
import { buildKitSlots, isKitEmpty, slotsOfGroup, staleChannelNotes, summarizeKitSlots } from './kitSlots';
import { useKitActions } from './useKitActions';
import { useKitViewer } from './useKitViewer';
import { KitSummary } from './KitSummary';
import { KitEmptyHead } from './KitEmptyHead';
import { KitGroup } from './KitGroup';
import { KitSlotCard } from './KitSlotCard';
import { KitCoverSlot } from './KitCoverSlot';
import { KitZipSlot } from './KitZipSlot';
import { KitStaleNotes } from './KitStaleNotes';
import { isAddedAsset } from '../../addedAssets/addedAssetFacts';
import { WatermarkBanner } from '../../../watermarkUpsell/files/WatermarkBanner';
import { fileHasWatermark } from '../../../watermarkUpsell/files/watermarkedFile';
import { CoverRedoPanel } from './coverRedo/CoverRedoPanel';
import { useCoverRedo } from './coverRedo/useCoverRedo';
import { workingEtaSec } from './coverRedo/workingEtaSec';
import { DateRedoPanel } from './dateRedo/DateRedoPanel';
import { useDateRedo } from './dateRedo/useDateRedo';
import { KitProfileUseRelease } from '../../profile/kit/KitProfileUseRelease';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Kit tab of the release page (design assets-rethink b1, b4, d2, d3; owner decisions
 * 2026-10-09). It replaces the Assets tab and shows only the keepers (fym-shared `assetKinds.ts`):
 * a fixed grid of slots in three groups, every slot visible also before it is made, the summary
 * with "Make the N missing ones", one binary badge per one-off channel (the publish records, also
 * the manual marks "I added it" / "I set it"), the kit zip (keepers only) and the asset viewer.
 * The post files of the song are on the Posts tab.
 */
const KitTabComponent: React.FC<ReleaseTabProps> = ({ accountId, item, release }) => {
  const data = useKitPoll({ accountId, catalogItemUuid: item.uuid, release });
  const { canRenderFullSongVideos } = useMusic24Capabilities();
  const slots = useMemo(
    () => buildKitSlots({ item, release: data, canRenderFullSongVideos }),
    [canRenderFullSongVideos, data, item]
  );
  const { data: publishData } = useGetAssetPublishesQuery({ accountId, catalogItemUuid: item.uuid });
  const publishes = useMemo(() => publishData?.publishes ?? [], [publishData]);
  const summary = useMemo(() => summarizeKitSlots(slots, publishes, item.uuid), [item.uuid, publishes, slots]);
  const notes = useMemo(() => staleChannelNotes(slots, publishes, item.uuid), [item.uuid, publishes, slots]);
  // An added video is not in the kit zip (spec 2026-10-09 D6).
  const fileCount = useMemo(
    () => slots.reduce((sum, one) => sum + one.readyAssets.filter((asset) => !isAddedAsset(asset)).length, 0),
    [slots]
  );
  const watermarkedCount = useMemo(
    () =>
      slots.reduce(
        (sum, one) => sum + one.readyAssets.filter((asset) => !isAddedAsset(asset) && fileHasWatermark(asset)).length,
        0
      ),
    [slots]
  );
  const actions = useKitActions({ accountId, item, slots });
  const viewer = useKitViewer({ accountId, catalogItemUuid: item.uuid, slots });
  const coverRedo = useCoverRedo({ accountId, catalogItemUuid: item.uuid });
  const dateRedo = useDateRedo({ accountId, catalogItemUuid: item.uuid });
  const etaSec = useMemo(() => workingEtaSec(data.assets), [data]);
  // The view mark clears "N new" of the Releases list, as the old Assets tab did.
  const now = useMemo(() => new Date(), []);
  useReleaseViewMark({ accountId, catalogItemUuid: item.uuid, now });

  // The Posts menu badge counts the failed keepers too: a Kit that shows a failure marks it seen.
  const { markSeen } = useFailuresSeen(accountId);
  const hasFailure = summary.failed > 0;
  useEffect(() => {
    if (hasFailure) {
      markSeen();
    }
  }, [hasFailure, markSeen]);

  return (
    <div className={styles.tab} data-testid="release-kit">
      {/* design: trial-upsell a4. It hides at 0 watermarked files. */}
      <WatermarkBanner className="mb-0" />
      {/* design: kit-redo-cover a, c — the cover of the song changed after some files were made. */}
      <CoverRedoPanel accountId={accountId} catalogItemUuid={item.uuid} redo={coverRedo} etaSec={etaSec} />
      {/* The release date of the song changed after some files were made (fym-shared `dateRedo.ts`). */}
      <DateRedoPanel accountId={accountId} catalogItemUuid={item.uuid} redo={dateRedo} etaSec={etaSec} />
      {isKitEmpty(slots) ? (
        <KitEmptyHead
          title={item.title}
          canAct={actions.canAct}
          isMaking={actions.isMaking}
          onMakeKit={actions.makeKit}
          onOpenStudio={actions.openStudio}
        />
      ) : (
        <KitSummary
          counts={summary}
          missingCount={actions.missingCount}
          missingBlocks={actions.missingBlocks}
          missingCost={actions.missingCost}
          creditsLeft={actions.creditsLeft}
          canAct={actions.canAct}
          isMaking={actions.isMaking}
          onMakeMissing={actions.makeMissing}
        />
      )}
      {/* The tour spotlights the slot grid at the kit stop. */}
      <div className={styles.tab} data-tour="kit">
        {KIT_SLOT_GROUPS.map((group) => (
          <KitGroup key={group} group={group}>
            {/* design artist-profile c3: the Profile of the artist from this release. */}
            {group === 'profiles' && actions.canAct && (
              <KitProfileUseRelease accountId={accountId} catalogItemUuid={item.uuid} title={item.title} />
            )}
            {slotsOfGroup(slots, group).map((view) =>
              view.slot.id === 'cover' ? (
                <KitCoverSlot key="cover" accountId={accountId} item={item} view={view} canAct={actions.canAct} />
              ) : (
                <KitSlotCard
                  key={view.slot.id}
                  accountId={accountId}
                  item={item}
                  view={view}
                  publishes={publishes}
                  actions={actions}
                  onOpen={viewer.openSlot}
                  oldCoverUuids={coverRedo.oldCoverUuids}
                />
              )
            )}
            {group === 'profiles' && (
              <KitZipSlot
                accountId={accountId}
                catalogItemUuid={item.uuid}
                fileCount={fileCount}
                watermarkedCount={watermarkedCount}
                songTitle={item.title}
              />
            )}
          </KitGroup>
        ))}
      </div>
      <KitStaleNotes notes={notes} />
      {viewer.viewer !== null && (
        <AssetViewer
          accountId={accountId}
          song={item}
          items={viewer.items}
          startKey={viewer.viewer.key}
          startRevision={viewer.viewer.revision}
          onClose={viewer.close}
          onShownChange={viewer.onShownChange}
        />
      )}
    </div>
  );
};

export const KitTab = TypedMemo(KitTabComponent);
